import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { format } from 'date-fns';

/**
 * Customer Wallet Balances Modal
 * Shows breakdown of all customer wallet balances with detailed metrics
 */
const CustomerWalletBalancesModal = ({ open, onClose, walletBalances, summary }) => {
  const formatAmount = (amount) => {
    const n = Number(amount);
    const value = Number.isFinite(n) ? n : 0;
    return `AED ${value.toLocaleString('en-AE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '-';
    return format(d, 'MMM dd, yyyy HH:mm');
  };

  const safeSummary = summary || {};
  const safeWalletBalances = Array.isArray(walletBalances) ? walletBalances : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AccountBalanceWalletIcon color="secondary" />
            <Typography variant="h6">Customer Wallet Balances</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Summary Section */}
        <Box mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="h6" color="secondary.main" fontWeight="600" gutterBottom fontSize="0.9rem">
            Total Balance: {formatAmount(safeSummary.totalBalance)}
          </Typography>
          <Box display="flex" gap={3} mt={1}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                Customers with Balance
              </Typography>
              <Typography variant="body2" fontWeight="600" fontSize="0.8rem">
                {safeSummary.customersWithBalance || 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                Average Balance per Customer
              </Typography>
              <Typography variant="body2" fontWeight="600" color="info.main" fontSize="0.8rem">
                {formatAmount(safeSummary.averageBalance)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Info Message */}
        <Paper
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: 'info.50',
            borderLeft: 4,
            borderColor: 'info.main',
          }}
        >
          <Typography variant="body2" color="info.dark" fontWeight="medium" fontSize="0.75rem">
            💡 These balances represent customer funds held by Thaklis (Liability).
            Customers deposit money into their wallets and use it to pay for services.
          </Typography>
        </Paper>

        {/* Customer Wallet Balances Table */}
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#1a7f8f', '& .MuiTableCell-root': { color: 'white' } }}>
                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}><strong>Customer Name</strong></TableCell>
                <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}><strong>Current Balance</strong></TableCell>
                <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}><strong>Total Deposited</strong></TableCell>
                <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}><strong>Total Spent</strong></TableCell>
                <TableCell sx={{ fontSize: '0.75rem', py: 1 }}><strong>Last Top-up Date</strong></TableCell>
                <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}><strong>Last Top-up Amount</strong></TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1 }}><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {safeWalletBalances.map((wallet) => (
                <TableRow key={wallet.customerId} hover>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" fontWeight="500" fontSize="0.8rem">
                      {wallet.customerName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <Typography variant="body2" fontWeight="600" color="secondary.main" fontSize="0.8rem">
                      {formatAmount(wallet.walletBalance)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <Typography variant="body2" color="success.main" fontSize="0.8rem">
                      {formatAmount(wallet.totalDeposited)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                      {formatAmount(wallet.totalSpent)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" fontSize="0.75rem">
                      {formatDate(wallet.lastTopupDate)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <Typography variant="body2" color="info.main" fontSize="0.8rem">
                      {formatAmount(wallet.lastTopupAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ py: 1 }}>
                    <Chip
                      label={wallet.status}
                      size="small"
                      color="success"
                      sx={{ fontSize: '0.7rem', height: '22px' }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerWalletBalancesModal;
